/**
 * Facebook SDK initialization
 *
 * Loads the Facebook JavaScript SDK dynamically using the Meta App ID
 * from the VITE_META_APP_ID environment variable. This replaces the
 * previously hardcoded SDK snippet in index.html.
 */
import { META_GRAPH_API_VERSION } from '../config/meta';

/**
 * FB Login for Business V2 response payload (Embedded Signup, Instagram Onboarding).
 *
 * When `config_id` is passed to `FB.login`, Meta returns a `code` (NOT an access
 * token) the server then exchanges via `/oauth/access_token` with the same
 * `code_verifier` — or, with `override_default_response_type: true`, a regular
 * code-grant payload. We use the latter so the server-side exchange path stays
 * the same as the legacy OAuth.
 *
 * For WhatsApp Embedded Signup, Meta also POSTs a `session_info_response`
 * window-message with `waba_id` and `phone_number_id` BEFORE the FB.login
 * callback fires. Captured in useWhatsAppEmbeddedSignup.
 */
export interface FbV2LoginResponse {
  authResponse?: {
    code?: string;
    accessToken?: string;
    userID?: string;
    expiresIn?: number;
  };
  status?: 'connected' | 'not_authorized' | 'unknown';
}

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: {
      init: (params: {
        appId: string;
        cookie: boolean;
        xfbml: boolean;
        version: string;
      }) => void;
      // Add more FB SDK methods as needed
      getLoginStatus: (callback: (response: unknown) => void) => void;
      // Legacy OAuth login (`scope`) AND FB Login for Business V2 (`config_id`).
      // For Embedded Signup V2 we pass `{ config_id, response_type: 'code',
      // override_default_response_type: true }`; the server then exchanges the
      // returned `code` for a long-lived token.
      login: (
        callback: (response: FbV2LoginResponse) => void,
        options?: {
          scope?: string;
          config_id?: string;
          response_type?: 'code' | 'token' | 'token,signed_request,graph_domain';
          override_default_response_type?: boolean;
          /** Classic dialog only: ask again for permissions declined last time. */
          auth_type?: 'rerequest' | 'reauthenticate' | 'reauthorize';
          return_scopes?: boolean;
          extras?: {
            /**
             * Embedded Signup prefill: what the merchant would otherwise type
             * into Meta's "business information" and "phone" steps.
             */
            setup?: {
              solutionID?: string;
              business?: { name?: string; email?: string; phone?: { code?: number; number?: string }; website?: string; address?: { streetAddress1?: string; city?: string; state?: string; zipPostal?: string; country?: string }; timezone?: string };
              phone?: { displayName?: string; category?: string; description?: string };
            };
            sessionInfoVersion?: string;
            version?: string;
            featureType?: string;
            features?: Array<{ name: string }>;
          };
        }
      ) => void;
    };
  }
}

/**
 * The app the SDK runs as.
 *
 * WhatsApp Embedded Signup opens `FB.login({ config_id })`, and a
 * configuration belongs to ONE app — passing a config from a different app
 * than `FB.init` was given fails inside Meta's dialog, where our code never
 * sees it. So when WhatsApp is onboarded on its own app, that app is the one
 * to init as — and every token this SDK mints then belongs to THAT app. The
 * Facebook/Instagram connect (`useMetaPopupConnect`) asks
 * `sdkServesPrimaryMetaApp()` before using the popup: a token from the
 * WhatsApp app cannot be exchanged on the primary app's secret, so on a
 * two-app deployment that connect takes the server-side redirect instead.
 *
 * VITE_META_WA_APP_ID unset — the ordinary single-app case — leaves this
 * exactly as it was.
 */
function sdkAppId(): string | undefined {
  return import.meta.env.VITE_META_WA_APP_ID || import.meta.env.VITE_META_APP_ID;
}

/**
 * True when a token minted by this page's SDK belongs to the primary Meta app
 * (the one the server exchanges Facebook/Instagram grants on): no WhatsApp
 * app configured, or the same id for both.
 */
export function sdkServesPrimaryMetaApp(): boolean {
  const wa = (import.meta.env.VITE_META_WA_APP_ID || '').toString().trim();
  const primary = (import.meta.env.VITE_META_APP_ID || '').toString().trim();
  return !wa || wa === primary;
}

/**
 * Resolves true once `window.FB` exists, false if it never will.
 *
 * The script is injected `async defer`, so there is a window — right after
 * the page loads — where the SDK is ON ITS WAY but `window.FB` is still
 * undefined. A caller that checks once in that window concludes the SDK is
 * unavailable, and WhatsApp Embedded Signup then silently took the legacy
 * redirect instead: a different auth flow, reached by accident, which on a
 * deployment whose redirect URIs are not whitelisted dead-ends at Meta's
 * "URL Blocked". Nothing on screen said a fallback had happened.
 *
 * Null until initFacebookSdk runs, which is how "never started" (no app id
 * configured) stays distinguishable from "still loading".
 */
let sdkReady: Promise<boolean> | null = null;

/**
 * Await the SDK rather than sampling it once.
 *
 * Resolves false when the SDK was never started, or did not arrive within
 * `timeoutMs` — an ad blocker or a CSP refusing connect.facebook.net is the
 * common cause, and it is a real answer, not a reason to quietly try
 * something else.
 */
export function whenFacebookSdkReady(timeoutMs = 8000): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.FB) return Promise.resolve(true);
  if (!sdkReady) return Promise.resolve(false);
  return Promise.race([
    sdkReady,
    new Promise<boolean>((resolve) => {
      setTimeout(() => resolve(Boolean(window.FB)), timeoutMs);
    }),
  ]);
}

export function initFacebookSdk(): void {
  const appId = sdkAppId();

  if (!appId) {
    console.warn('[BuzzBit X] VITE_META_APP_ID not set — Facebook SDK will not load');
    return;
  }

  let settle: (ok: boolean) => void = () => {};
  sdkReady = new Promise<boolean>((resolve) => {
    settle = resolve;
  });

  window.fbAsyncInit = function () {
    window.FB.init({
      appId,
      cookie: true,
      xfbml: true,
      version: META_GRAPH_API_VERSION,
    });
    settle(true);
  };

  // Dynamically load the SDK script
  const script = document.createElement('script');
  script.src = 'https://connect.facebook.net/en_US/sdk.js';
  script.async = true;
  script.defer = true;
  script.crossOrigin = 'anonymous';
  // A blocked or failed script never fires fbAsyncInit, so without this the
  // promise would hang until the caller's timeout. Answering immediately
  // turns "blocked by an extension" into a prompt, legible failure.
  script.onerror = () => settle(false);
  document.body.appendChild(script);
}
